/**
 * ReceptionPage — Page de réception fournisseurs pour le POS
 *
 * 3 onglets internes (état React, pas de sous-routes) :
 * 1. Import — upload documents fournisseurs, parsing IA
 * 2. Vérification — revue des lignes, scan, DLC, approbation/rejet (crée produit+lot)
 * 3. Mise en Rayon — lots en réserve, transfert, photo étiquette, impression
 *
 * useBarcodeScan au niveau page, routé vers l'onglet actif.
 * Accessible via POS > Gestion > Réception (workstation STOCKS).
 *
 * Paramètres d'URL (05/08/2026) — permettent d'arriver directement sur un produit,
 * p. ex. depuis une alerte de réapprovisionnement :
 *   ?tab=rayon        onglet d'arrivée (import | verification | rayon)
 *   ?q=<recherche>    pré-remplit la recherche de l'onglet Mise en rayon
 * Les onglets restent de l'état React : l'URL n'est lue qu'au montage.
 */
import { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MapPin, PackagePlus } from 'lucide-react';
import { useBarcodeScan } from '../../hooks/useBarcodeScan';
import { useSmartBack } from '../../hooks/useSmartBack';
import ImportTab from '../../components/reception/ImportTab';
import DeliveryVerificationTab from '../../components/reception/DeliveryVerificationTab';
import MiseEnRayonTab from '../../components/reception/MiseEnRayonTab';

type ReceptionTab = 'import' | 'verification' | 'rayon';

const isReceptionTab = (v: string | null): v is ReceptionTab =>
  v === 'import' || v === 'verification' || v === 'rayon';

export default function ReceptionPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Retour là d'où l'on vient (caisse OU accueil tactile) ; /pos si ouverte directement.
  const goBack = useSmartBack('/pos');

  // Onglet et recherche d'arrivée, lus UNE fois au montage (l'onglet reste piloté
  // par l'état React ensuite — on ne réécrit pas l'URL à chaque changement).
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<ReceptionTab>(
    isReceptionTab(tabParam) ? tabParam : 'import',
  );
  const [initialRayonSearch] = useState(() => searchParams.get('q') || '');

  // Badges
  const [pendingDeliveriesCount, setPendingDeliveriesCount] = useState(0);
  const [backroomCount, setBackroomCount] = useState(0);
  const [activeJobsCount, setActiveJobsCount] = useState(0);

  // Barcode passé au tab actif
  const [scannedBarcode, setScannedBarcode] = useState<string | null>(null);

  // Delivery sélectionnée
  const [selectedDeliveryId, setSelectedDeliveryId] = useState<string | null>(null);

  // Refresh keys pour forcer le rechargement quand on change d'onglet
  const [verificationRefreshKey, setVerificationRefreshKey] = useState(0);
  const [rayonRefreshKey, setRayonRefreshKey] = useState(0);

  // Barcode scan global
  useBarcodeScan({
    onScan: useCallback((barcode: string) => {
      setScannedBarcode(barcode);
    }, []),
    enabled: true,
  });

  const clearBarcode = useCallback(() => setScannedBarcode(null), []);

  // Callback quand un parsing termine → basculer sur Vérification
  const handleSelectDelivery = useCallback((deliveryId: string) => {
    setSelectedDeliveryId(deliveryId);
    setVerificationRefreshKey(k => k + 1);
    setActiveTab('verification');
  }, []);

  // Callback vérification → mise en rayon
  const handleDeliveryApplied = useCallback(() => {
    setSelectedDeliveryId(null);
    setRayonRefreshKey(k => k + 1);
    setActiveTab('rayon');
  }, []);

  const handleDeliveryChange = useCallback((id: string | null) => {
    setSelectedDeliveryId(id);
  }, []);

  // Changer d'onglet avec refresh
  const switchTab = useCallback((tab: ReceptionTab) => {
    if (tab === 'verification') {
      setVerificationRefreshKey(k => k + 1);
    } else if (tab === 'rayon') {
      setRayonRefreshKey(k => k + 1);
    }
    setActiveTab(tab);
  }, []);

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={goBack}
            className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors min-h-[48px]"
          >
            ← Retour
          </button>
          <h1 className="text-lg font-bold text-gray-900">Reception</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/storage/putaway')}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg transition-colors min-h-[48px]"
          >
            <PackagePlus className="w-4 h-4" />
            Rangement
          </button>
          <button
            onClick={() => navigate('/storage/locations')}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors min-h-[48px]"
          >
            <MapPin className="w-4 h-4" />
            Emplacements
          </button>
        </div>
      </header>

      {/* Onglets */}
      <div className="bg-white border-b border-gray-200 px-4">
        <div className="flex gap-1">
          <button
            onClick={() => switchTab('import')}
            className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 min-h-[48px] ${
              activeTab === 'import'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Import
            {activeJobsCount > 0 && (
              <span className="px-1.5 py-0.5 bg-blue-500 text-white text-xs rounded-full font-bold">
                {activeJobsCount}
              </span>
            )}
          </button>
          <button
            onClick={() => switchTab('verification')}
            className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 min-h-[48px] ${
              activeTab === 'verification'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Verification
            {pendingDeliveriesCount > 0 && (
              <span className="px-1.5 py-0.5 bg-yellow-500 text-white text-xs rounded-full font-bold">
                {pendingDeliveriesCount}
              </span>
            )}
          </button>
          <button
            onClick={() => switchTab('rayon')}
            className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 min-h-[48px] ${
              activeTab === 'rayon'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Mise en Rayon
            {backroomCount > 0 && (
              <span className="px-1.5 py-0.5 bg-green-600 text-white text-xs rounded-full font-bold">
                {backroomCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Contenu des onglets */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 max-w-4xl mx-auto">
          <div style={{ display: activeTab === 'import' ? 'block' : 'none' }}>
            <ImportTab
              onSelectDelivery={handleSelectDelivery}
              onActiveJobsCountChange={setActiveJobsCount}
            />
          </div>
          <div style={{ display: activeTab === 'verification' ? 'block' : 'none' }}>
            <DeliveryVerificationTab
              key={verificationRefreshKey}
              deliveryId={selectedDeliveryId}
              onDeliveryChange={handleDeliveryChange}
              scannedBarcode={activeTab === 'verification' ? scannedBarcode : null}
              onBarcodeClear={clearBarcode}
              onDeliveryApplied={handleDeliveryApplied}
              onPendingCountChange={setPendingDeliveriesCount}
            />
          </div>
          <div style={{ display: activeTab === 'rayon' ? 'block' : 'none' }}>
            <MiseEnRayonTab
              key={rayonRefreshKey}
              scannedBarcode={activeTab === 'rayon' ? scannedBarcode : null}
              onBarcodeClear={clearBarcode}
              onBackroomCountChange={setBackroomCount}
              initialSearch={initialRayonSearch}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
